import { useState } from 'react';
import { Tooltip } from '@material-ui/core';
import styled from 'styled-components';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { cn, LBButton, LBInput, LBXIcon } from '@linearb/buzz';

const Container = styled(Flex)`
  z-index: 9999;
`;

const SaveButtonTooltip = ({ saveChanges, reportName, close, isSaving }) => {
  return (
    <Container direction='column' padding='small' width='20rem'>
      <LBButton variant='ghost' size='icon' onClick={close}>
        <LBXIcon />
        <span className='sr-only'>Close</span>
      </LBButton>
      <Text size='large' weight='400' color='black'>
        Save this dashboard?
      </Text>
      <Text size='medium' weight='300' pt='1rem' pb='1rem' lineHeight='1.5'>
        Saving these changes will replace the current dashboard "{reportName}"
      </Text>
      <Flex>
        <LBButton
          disabled={isSaving}
          onClick={saveChanges}
          data-cy='confirmation-save-button'
        >
          Save
        </LBButton>
        <LBButton variant='outline' onClick={close}>
          Cancel
        </LBButton>
      </Flex>
    </Container>
  );
};

export const SaveButton = ({
  saveChanges,
  reportName,
  isNew,
  isDashboardNameValid
}) => {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  return (
    <Tooltip
      interactive
      open={open}
      title={
        <SaveButtonTooltip
          saveChanges={async () => {
            setIsSaving(true);
            await saveChanges();
            setOpen(false);
            setIsSaving(false);
          }}
          reportName={reportName}
          // @ts-ignore
          placement='bottom'
          isSaving={isSaving}
          close={() => setOpen(false)}
        />
      }
    >
      <div>
        <LBButton
          data-cy='save-custom-dashboard'
          disabled={!isDashboardNameValid}
          onClick={isNew ? () => saveChanges() : () => setOpen(true)}
        >
          Save
        </LBButton>
      </div>
    </Tooltip>
  );
};

const SaveAsButtonTooltip = ({
  saveChanges,
  reportName,
  close,
  isDashboardNameValid,
  validateName
}) => {
  const tempName = `${reportName} (copy)`;
  const [name, setName] = useState(tempName);
  const isCurrentNameValid = validateName(name || tempName);
  const hasError = !isDashboardNameValid || !isCurrentNameValid;
  return (
    <Container direction='column' padding='small' width='30rem'>
      <LBButton variant='ghost' size='icon' onClick={close}>
        <LBXIcon />
        <span className='sr-only'>close</span>
      </LBButton>
      <Text size='large' weight='400' color='black'>
        Save this as a new dashboard
      </Text>
      <Text size='medium' weight='300' pt='1rem' pb='1rem' lineHeight='1.5'>
        Name your new dashboard:
      </Text>
      <LBInput
        placeholder={tempName}
        value={name}
        onChange={event => setName(event.target.value)}
        className={cn('w-full', { 'border-destructive': hasError })}
      />
      {hasError && (
        <p className='mt-8 text-sm text-destructive'>
          This name already exists
        </p>
      )}

      <Flex mt={hasError ? 'medium' : 'small'}>
        <LBButton
          disabled={hasError}
          onClick={() => saveChanges(name || tempName)}
        >
          Save
        </LBButton>
        <LBButton variant='outline' onClick={close}>
          Cancel
        </LBButton>
      </Flex>
    </Container>
  );
};

export const SaveAsButton = ({
  text,
  variant = 'outline',
  saveChanges,
  reportName,
  isDashboardNameValid,
  validateName
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      interactive
      open={open}
      title={
        <SaveAsButtonTooltip
          saveChanges={name => {
            saveChanges(name);
            setOpen(false);
          }}
          reportName={reportName}
          // @ts-ignore
          placement='bottom'
          close={() => setOpen(false)}
          isDashboardNameValid={isDashboardNameValid}
          validateName={validateName}
        />
      }
    >
      <div>
        {/* @ts-ignore */}
        <LBButton variant={variant} onClick={setOpen}>
          {text}
        </LBButton>
      </div>
    </Tooltip>
  );
};
